import {
  DurationString,
  getDurationFromTimeRangeStart,
  isDurationString,
  isTimeRangeInputValueAbsolute,
  TimeRangeInputValue,
  TimeRangeValue,
} from '@chronosphereio/core';
import { isValid } from 'date-fns';
import { z } from 'zod/v4';

// TODO: use regex to validate duration
export const durationSchema = z.string();

export const timeOptionSchema = z.enum(['default', 'monthly']);

export type NamedTimeOption = z.infer<typeof timeOptionSchema>;

export const timeDashboardSchema = z.object({
  /**
   * Name time options for predefined custom ranges like 'monthly'.
   */
  options: timeOptionSchema.optional(),
  /**
   * Absolute start time, when defined this takes priority over saved 'duration'.
   */
  start: z.string().optional(),
  /**
   * Absolute end time, must be defined with 'start' time to be valid.
   */
  end: z.string().optional(),
});

export interface DashboardTimeDefinition extends z.TypeOf<typeof timeDashboardSchema> {}

/**
 * Persists a time range to a dashboard spec, clearing the opposite field
 * to ensure only duration (relative) or time (absolute) is set.
 */
export function applyTimeRangeToSpec(
  spec: { duration?: DurationString; time?: DashboardTimeDefinition },
  timeRange: TimeRangeInputValue
): void {
  if (isTimeRangeInputValueAbsolute(timeRange)) {
    spec.time = {
      start: timeRange.start.toISOString(),
      end: timeRange.end.toISOString(),
    };
    if (spec.duration !== undefined) {
      delete spec.duration;
    }
    return;
  }

  const pastDuration = getDurationFromTimeRangeStart(timeRange);
  if (pastDuration) {
    spec.duration = pastDuration;
    if (spec.time !== undefined) {
      delete spec.time;
    }
    return;
  }

  // This means we got some unknown time range value, so just leave as a no-op for now
}

/**
 * Converts a TimeRangeInputValue to a DashboardTimeDefinition
 */
export function timeRangeToDashboardTime(timeRange: TimeRangeInputValue): DashboardTimeDefinition | undefined {
  if (isTimeRangeInputValueAbsolute(timeRange)) {
    return { start: timeRange.start.toISOString(), end: timeRange.end.toISOString() };
  }

  const startDuration = getDurationFromTimeRangeStart(timeRange);
  if (startDuration !== undefined) {
    return { start: startDuration };
  }

  return undefined;
}

/**
 * Converts a DashboardTimeDefinition to a TimeRangeValue
 */
export function dashboardTimeToTimeRange(time: DashboardTimeDefinition): TimeRangeValue | undefined {
  if (time.start && isDurationString(time.start) && !time.end) {
    return { pastDuration: time.start };
  }
  if (time.start && time.end) {
    const start = new Date(time.start);
    const end = new Date(time.end);
    if (!isValid(start) || !isValid(end)) {
      return undefined;
    }
    return { start, end };
  }
  return undefined;
}
